import React from 'react';
import { useSelector } from 'react-redux';
import { LAYOUT_CONFIG } from '../../constants';
import { validateHeightConstraint, withErrorBoundary } from '../../js/text/validationUtils';
import MobileFloatingToolbar from './components/MobileFloatingToolbar';
import MobileTextModal from './components/MobileTextModal';
import { useResponsive } from './hooks/useResponsive';
import { useTextEditor } from './hooks/useTextEditor';
import { useTextEditorContext } from './TextEditorContext';

function TextEditorComponent() {
  const {
    // State
    text,
    setText,
    color,
    fontFamily,
    scale,
    rotate,
    textList,
    activeTextId,
    showFloatingInput,
    setShowFloatingInput,
    setShouldMaintainFocus,
    inputRef,
    isSubmitting, // Add submission guard state
    // Functions
    focusInput,
    preventScaleReset,
    findTextByIdMemo,
    handleSubmit,
    handleUpdateText,
    handleFontChange,
    handleColorChange,
    handleScaleChange,
    handleRotateChange,
    handleDeleteText,
    handleReset,
    handleNewText,
    deselectText,
    // State management functions
    restoreTextSelection,
  } = useTextEditor();

  // State for managing expanded panels
  const [activePanel, setActivePanel] = React.useState(null);
  const { showText } = useSelector((state) => state.control);

  // Get context for sharing state
  const { updateTextEditorState } = useTextEditorContext();

  // Auto focus color panel when text is selected or new text is added
  React.useEffect(() => {
    if (activeTextId) {
      const foundText = findTextByIdMemo(activeTextId);
      if (foundText) {
        // Text is selected, auto focus color panel
        setActivePanel('color');
      } else {
        setActivePanel(null);
      }
    } else if (showFloatingInput) {
      // New text is being added, auto focus color panel
      setActivePanel('color');
    } else {
      // No active text, close all panels
      setActivePanel(null);
    }
  }, [activeTextId, showFloatingInput]);

  // Update context when state changes
  React.useEffect(() => {
    updateTextEditorState({ activeTextId, text, showFloatingInput });
  }, [activeTextId, text, showFloatingInput]);

  // Set global functions for TextActions to access
  React.useEffect(() => {
    window.handleNewText = handleNewText;
    window.handleReset = handleReset;
    window.handleDeleteText = handleDeleteText;
    window.deselectText = deselectText;

    // Cleanup on unmount
    return () => {
      delete window.handleNewText;
      delete window.handleReset;
      delete window.handleDeleteText;
      delete window.deselectText;
    };
  }, [handleNewText, handleReset, handleDeleteText, deselectText]);

  // Responsive design hook
  const { isMobile } = useResponsive();

  // Validate height constraint
  React.useEffect(() => {
    const isValid = validateHeightConstraint(LAYOUT_CONFIG.TOTAL_HEIGHT, 130);
    if (!isValid) {
      console.error('TextEditor height constraint violation detected');
    }
  }, []);

  return (
    <div
      className="text-editor-container"
      style={{
        height: '100%',
        display: 'flex',
        position: 'relative',
      }}
    >
      {/* Modern Floating Toolbar - Match Image Design */}
      <MobileFloatingToolbar
        isMobile={isMobile}
        hasActiveText={showText && !showFloatingInput}
        activePanel={activePanel}
        setActivePanel={setActivePanel}
        scale={scale}
        rotate={rotate}
        handleScaleChange={handleScaleChange}
        handleRotateChange={handleRotateChange}
        handleColorChange={handleColorChange}
        handleFontChange={handleFontChange}
        handleDeleteText={handleDeleteText}
        focusInput={focusInput}
        handleNewText={handleNewText}
        color={color}
        fontFamily={fontFamily}
        textList={textList}
        activeTextId={activeTextId}
        handleUpdateText={handleUpdateText}
        findTextByIdMemo={findTextByIdMemo}
        setShouldMaintainFocus={setShouldMaintainFocus}
        preventScaleReset={preventScaleReset}
        setShowFloatingInput={setShowFloatingInput}
        deselectText={deselectText}
        text={text}
        setText={setText}
        restoreTextSelection={restoreTextSelection}
      />

      {/* Mobile Text Modal - Full Screen Overlay for Mobile */}
      <MobileTextModal
        showFloatingInput={showFloatingInput}
        setShowFloatingInput={setShowFloatingInput}
        text={text}
        setText={setText}
        activeTextId={activeTextId}
        isSubmitting={isSubmitting}
        inputRef={inputRef}
        focusInput={focusInput}
        setShouldMaintainFocus={setShouldMaintainFocus}
        handleSubmit={handleSubmit}
        handleUpdateText={handleUpdateText}
        findTextByIdMemo={findTextByIdMemo}
        textList={textList}
      />

      {/* Text Elements List - Positioned Top Right */}
      {/* Removed text list as requested by user */}
    </div>
  );
}

// Export TextEditor with error boundary
export const TextEditor = withErrorBoundary(
  TextEditorComponent,
  <div
    style={{
      height: '130px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0, 20, 30, 0.95)',
      border: '1px solid #FF4444',
      borderRadius: '12px',
      color: '#FF4444',
      fontSize: '14px',
      fontWeight: '500',
    }}
  >
    TextEditor Error: Please refresh the page
  </div>,
);
